import { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { StatCard } from '@/components/StatCard';
import { StatusBadge } from '@/components/StatusBadge';
import { HistoryCharts } from '@/components/resources/StatsCharts';
import { api } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { isRunningContainer } from '@/lib/containerActions';
import { usePolledData } from '@/lib/hooks/usePolledData';

const POLL_MS = 30_000;
const RANGES = [
  { value: '1h', label: 'Last hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
];
const selectClass =
  'h-9 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function ContainerStatsTab({ containerId, status }: { containerId: number; status: string }) {
  const [range, setRange] = useState('1h');

  if (!isRunningContainer(status)) {
    return <EmptyState as="h3" icon={BarChart3} title="No stats while the container is not running" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <select aria-label="Time range" value={range} onChange={(e) => setRange(e.target.value)} className={selectClass}>
          {RANGES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <StatsBody containerId={containerId} range={range} />
    </div>
  );
}

function StatsBody({ containerId, range }: { containerId: number; range: string }) {
  // Changing the range restarts the poll but keeps the previous result on screen until the new one arrives.
  const { data: result, error, loading, refetch } = usePolledData(
    async () => ({ range, stats: await api.containers.stats(containerId, { range }) }),
    POLL_MS,
    range,
  );
  const data = result?.stats;
  // A failed range change keeps the old data and shows the error banner instead of a pending state.
  const updating = result !== undefined && result.range !== range && error === null;

  if (data === undefined) {
    if (loading) {
      return (
        <div>
          <div aria-hidden className="space-y-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
              ))}
            </div>
            <div className="h-64 animate-pulse rounded-xl bg-muted" />
          </div>
          <p role="status" className="sr-only">
            Loading stats…
          </p>
        </div>
      );
    }
    return (
      <EmptyState
        as="h3"
        title="Could not load stats"
        description={error ?? undefined}
        action={
          <Button variant="outline" size="sm" onClick={refetch}>
            Retry
          </Button>
        }
      />
    );
  }

  const stale = error !== null && (
    <div
      role="status"
      className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
    >
      <span>Showing last known stats — refresh failed.</span>
      <Button variant="ghost" size="xs" onClick={refetch}>
        Retry
      </Button>
    </div>
  );

  const current = data.current;
  const history = data.history ?? [];
  if (!current) {
    return (
      <div className="space-y-4">
        {stale}
        <EmptyState as="h3" icon={BarChart3} title="Collecting data…" description="Stats appear after the next collection cycle." />
      </div>
    );
  }

  const hasLimit = current.memory_limit > 0;
  return (
    <div className="space-y-4" aria-busy={updating}>
      {stale}
      {updating && (
        <p role="status" className="text-xs text-muted-foreground">
          Updating range…
        </p>
      )}
      <div className={updating ? 'grid grid-cols-2 gap-4 opacity-60 transition-opacity lg:grid-cols-5' : 'grid grid-cols-2 gap-4 lg:grid-cols-5'}>
        <StatCard label="CPU" value={`${current.cpu_percent.toFixed(1)}%`} />
        <StatCard
          label="Memory"
          value={formatBytes(current.memory_used)}
          hint={hasLimit ? `of ${formatBytes(current.memory_limit)} · ${current.memory_percent.toFixed(1)}%` : 'no limit'}
        />
        <StatCard label="Network in" value={formatBytes(current.network_in_bytes)} hint="total since start" />
        <StatCard label="Network out" value={formatBytes(current.network_out_bytes)} hint="total since start" />
        <StatCard
          label="Health"
          value={
            current.health_status === 'none' ? (
              <span className="text-base font-medium text-muted-foreground">No healthcheck</span>
            ) : (
              <StatusBadge status={current.health_status} />
            )
          }
        />
      </div>
      <Card className={updating ? 'gap-3 p-4 opacity-60 transition-opacity' : 'gap-3 p-4'}>
        <h2 className="font-medium">History</h2>
        <HistoryCharts history={history} networkMode="counter" />
      </Card>
    </div>
  );
}
